import { lazy } from 'react';
import BackgroundColorWrapper from '@mediashop/base/pattern/atom/BackgroundColorWrapper';
import ContentWrapper from '../../../../base/pattern/atom/ContentWrapper';
import { BaseProps } from '@mediashop/app/bloomreach/types';
import { SKIP_RENDER } from '@mediashop/app/constants/semanticConstants';
import { AccordionItemPDP } from './types';
import { useGetProductBySlug } from '@mediashop/app/hooks/useGetProductBySlug';
import { lazyRetry } from '@mediashop/base/pattern/molecule/LazyComponent';
import { AssetTag } from '@mediashop/app/api/types/ClientProduct';
import { TeaserGridProps } from '../TeaserGrid';
import { AccordionProps } from '@mediashop/base/pattern/molecule/Accordion';
import { TextProps } from '@mediashop/base/pattern/atom/Text';

const AccordionPDPItem = lazy(() => lazyRetry(() => import('./AccordionItem')));

export type AccordionPDPProps = BaseProps & {
    contentType: string;
    items: AccordionItemPDP[];
    hideAttributes: boolean;
    hideDeliveryContents: boolean;
    hideDetails: boolean;
    hideDownloads: boolean;
    hideProductSafety: boolean;
    hideRatings: boolean;
    hideVideos: boolean;
    hideFaq: boolean;
    hideSpecialContent: boolean;
    separatorLineColorHex?: string;
    faq?: AccordionProps;
    specialContent?: TeaserGridProps | TextProps;
};

const componentName = 'accordion-pdp';

// eslint-disable-next-line complexity
function AccordionPDP({
    items,
    hideAttributes,
    hideDeliveryContents,
    hideDetails,
    hideDownloads,
    hideProductSafety,
    hideRatings,
    hideVideos,
    hideFaq,
    hideSpecialContent,
    separatorLineColorHex,
    faq,
    specialContent,
}: AccordionPDPProps): JSX.Element | null {
    const { product: foundProduct } = useGetProductBySlug();
    const productBySlug = foundProduct?.product;

    const assets = foundProduct?.product?.variants[0].assets || [];
    const downloadAssets = assets.filter((item) => item.tags.includes(AssetTag.DOWNLOAD));
    const ratingCount = productBySlug?.variants[0]?.attributes?.ratingCountLocalized ?? 0;
    const hasVideos =
        Boolean(foundProduct?.product?.variants[0]?.attributes?.youtubeVideoLink) ||
        Boolean(foundProduct?.product?.variants[0]?.attributes?.userVideoGuide);

    const hasSafetyInstructions =
        Boolean(foundProduct?.selectedVariant?.attributes?.manufacturer) ||
        Boolean(foundProduct?.selectedVariant?.attributes?.safetyInstructions) ||
        Boolean(foundProduct?.selectedVariant?.attributes?.hazardWarnings1?.length) ||
        Boolean(foundProduct?.selectedVariant?.attributes?.productSafetyPictograms?.length);

    const accordionItems = items.filter(
        // eslint-disable-next-line complexity
        (item) =>
            (item.accordionEntryType === 'details' && !hideDetails) ||
            (item.accordionEntryType === 'ratings' && ratingCount > 0 && !hideRatings) ||
            (item.accordionEntryType === 'deliveryContent' && !hideDeliveryContents) ||
            (item.accordionEntryType === 'attributes' && !hideAttributes) ||
            (item.accordionEntryType === 'downloads' && downloadAssets.length > 0 && !hideDownloads) ||
            (item.accordionEntryType === 'videos' && hasVideos && !hideVideos) ||
            (item.accordionEntryType === 'faq' && faq && !hideFaq) ||
            (item.accordionEntryType === 'specialContent' && specialContent && !hideSpecialContent) ||
            (item.accordionEntryType === 'productSafety' && hasSafetyInstructions && !hideProductSafety)
    );

    return productBySlug ? (
        <BackgroundColorWrapper backgroundColor="default">
            <ContentWrapper>
                <div className={`${componentName}__wrapper`}>
                    {accordionItems?.map((item) => (
                        <AccordionPDPItem
                            item={item}
                            key={`${foundProduct?.product?.key}-${item.accordionEntryType}`}
                            product={foundProduct}
                            separatorLineColorHex={separatorLineColorHex}
                            faq={faq}
                            specialContent={specialContent}
                        />
                    ))}
                </div>
            </ContentWrapper>
        </BackgroundColorWrapper>
    ) : (
        SKIP_RENDER
    );
}

export default AccordionPDP;
